import { QDataTable, QButton, QModal, QModalTitle, QModalContent, QTextField, QModalActions } from '@kw/quokka-ui';
import { Close } from '@mui/icons-material';
import { useCallback, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';

import { useFetchService } from '../../utils/fetchService';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { sendHttpRequest } from '../../utils/network.service';
import { formatDateTime } from './utils';

export const Organizations = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const { data = [], mutate } = useFetchService('/organizations/list', accessToken);

  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');

  const closeCreateModal = useCallback(() => {
    setCreateModalOpen(false);
    setNewOrgName('');
  }, []);

  const openCreateModal = useCallback(() => setCreateModalOpen(true), []);

  const validName = newOrgName.trim();
  const canCreate = validName.length > 0;

  const createOrganization = useCallback(async () => {
    if (canCreate) {
      await sendHttpRequest({
        path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations`,
        method: 'POST',
        body: { name: validName },
        successMessage: 'Organization created successfully',
        errorMessage: 'Error creating organization',
        enqueueSnackbar,
      });
    }
    closeCreateModal();
    mutate();
  }, [canCreate, validName, enqueueSnackbar, closeCreateModal, mutate]);

  const columns = useMemo(
    () => [
      { field: 'id', headerName: 'Organization ID' },
      { field: 'name', headerName: 'Name' },
      {
        field: 'settings',
        headerName: 'Settings',
        renderCell: row => (Array.isArray(row.settings) ? row.settings.join(', ') : ''),
      },
      { field: 'supportEmail', headerName: 'Support Email' },
      {
        field: 'updatedAt',
        headerName: 'Updated At',
        renderCell: row => (row.updatedAt ? formatDateTime(row.updatedAt) : ''),
      },
    ],
    [],
  );

  return (
    <>
      <QModal
        open={isCreateModalOpen}
        title={
          <QModalTitle onClose={closeCreateModal} closeIcon={<Close />}>
            Create Organization
          </QModalTitle>
        }
        content={
          <QModalContent>
            <QTextField
              value={newOrgName}
              onChange={e => setNewOrgName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && canCreate) createOrganization();
              }}
              label="Organization Name"
              fullWidth
              autoFocus
            />
          </QModalContent>
        }
        actions={
          <QModalActions>
            <QButton variant="outlined" onClick={closeCreateModal}>
              Cancel
            </QButton>
            <QButton variant="filled" disabled={!canCreate} onClick={createOrganization}>
              Create
            </QButton>
          </QModalActions>
        }
      />

      <QButton variant="filled" onClick={openCreateModal} sx={{ mb: 2 }}>
        Create Organization
      </QButton>

      <QDataTable emptyTableText="No Organizations Found" columns={columns} data={data} searchConfig={['id', 'name', 'supportEmail']} />
    </>
  );
};
